<template>
    <div id="content">
        <div class="page-wrap">
            <router-link to="/spheres" class="back-link">
                <i class="bi bi-chevron-left"></i>
                {{ $t('spheres.edit.backLink') }}
            </router-link>

            <div v-if="loading" class="state-loading">
                <div class="spinner-border" role="status"></div>
                <p>{{ $t('spheres.edit.loading') }}</p>
            </div>

            <div v-else-if="!sphere" class="state-warning">
                <i class="bi bi-exclamation-triangle"></i>
                {{ $t('spheres.edit.notFound') }}
            </div>

            <div v-else>
                <!-- Page header -->
                <div class="page-header">
                    <div class="page-header-icon">
                        <img v-if="headerIconSrc" :src="headerIconSrc" :alt="sphere.icon_slug || ''" />
                        <i v-else class="bi bi-collection"></i>
                    </div>
                    <div class="page-header-main">
                        <div class="page-header-titleline">
                            <h2 class="page-header-title">{{ sphere.name }}</h2>
                            <span class="page-header-slug">{{ sphere.slug }}</span>
                        </div>
                        <div class="page-header-meta">
                            <span
                                class="status-pill"
                                :class="sphere.enabled ? 'status-pill-on' : 'status-pill-off'"
                            >
                                <span class="status-dot"></span>
                                {{ sphere.enabled ? $t('spheres.edit.status.active') : $t('spheres.edit.status.disabled') }}
                            </span>
                            <span class="meta-item">
                                <i class="bi bi-list-ul"></i>
                                {{ $t('spheres.edit.meta.sections', { n: totalPreferences }) }}
                            </span>
                            <span class="meta-item">
                                <i class="bi bi-grid-3x3-gap"></i>
                                {{ $t('spheres.edit.meta.widgets', { n: sphere.widgets.length }) }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Section 1: Basic fields -->
                <section class="section-card">
                    <div class="section-header">
                        <div class="section-label">
                            <i class="bi bi-sliders"></i>
                            {{ $t('spheres.edit.basic.sectionLabel') }}
                        </div>
                        <div v-if="basicDirty" class="section-dirty">
                            <i class="bi bi-circle-fill"></i>
                            {{ $t('spheres.edit.basic.unsavedChanges') }}
                        </div>
                    </div>

                    <div class="section-body">
                        <div class="field-row">
                            <div class="field" style="flex: 2;">
                                <label class="field-label">
                                    <i class="bi bi-pencil-square"></i>
                                    {{ $t('spheres.edit.basic.name') }}
                                </label>
                                <input type="text" class="field-input" v-model="form.name" maxlength="127" />
                            </div>
                            <div class="field" style="flex: 1;">
                                <label class="field-label">
                                    <i class="bi bi-link-45deg"></i>
                                    {{ $t('spheres.edit.basic.slug') }}
                                </label>
                                <input
                                    type="text"
                                    class="field-input field-input-mono"
                                    v-model="form.slug"
                                    pattern="[a-z0-9-]+"
                                    maxlength="64"
                                />
                            </div>
                        </div>

                        <div class="field">
                            <label class="field-label">
                                <i class="bi bi-card-text"></i>
                                {{ $t('spheres.edit.basic.description') }}
                            </label>
                            <textarea
                                class="field-input"
                                v-model="form.description"
                                rows="2"
                                maxlength="255"
                                :placeholder="$t('spheres.edit.basic.descriptionPlaceholder')"
                            ></textarea>
                        </div>

                        <div class="field-row">
                            <div class="field">
                                <label class="field-label">
                                    <i class="bi bi-box"></i>
                                    {{ $t('spheres.edit.basic.objectSingular') }}
                                </label>
                                <input
                                    type="text"
                                    class="field-input"
                                    v-model="form.object_label_singular"
                                    maxlength="64"
                                />
                            </div>
                            <div class="field">
                                <label class="field-label">
                                    <i class="bi bi-boxes"></i>
                                    {{ $t('spheres.edit.basic.objectPlural') }}
                                </label>
                                <input
                                    type="text"
                                    class="field-input"
                                    v-model="form.object_label_plural"
                                    maxlength="64"
                                />
                            </div>
                        </div>

                        <div class="field-row">
                            <div class="field" style="flex: 0 0 220px;">
                                <label class="field-label">
                                    <i class="bi bi-arrow-down-up"></i>
                                    {{ $t('spheres.edit.basic.position') }}
                                </label>
                                <input
                                    type="number"
                                    class="field-input"
                                    v-model.number="form.position"
                                    min="0"
                                />
                            </div>
                            <div class="field field-toggle" style="flex: 1;">
                                <label class="field-label">
                                    <i class="bi bi-toggle-on"></i>
                                    {{ $t('spheres.edit.basic.state') }}
                                </label>
                                <label class="toggle">
                                    <input
                                        type="checkbox"
                                        v-model="form.enabled"
                                    />
                                    <span class="toggle-slider"></span>
                                    <span class="toggle-label">
                                        {{ form.enabled ? $t('spheres.edit.basic.enabledLabel') : $t('spheres.edit.basic.disabledLabel') }}
                                    </span>
                                </label>
                            </div>
                        </div>

                        <div class="field">
                            <label class="field-label">
                                <i class="bi bi-image"></i>
                                {{ $t('spheres.edit.basic.icon') }}
                            </label>
                            <IconPicker v-model="iconModel" />
                        </div>

                        <div class="section-actions">
                            <button
                                type="button"
                                class="btn-secondary"
                                :disabled="!basicDirty || saving"
                                @click="resetBasic"
                            >
                                {{ $t('spheres.edit.basic.cancel') }}
                            </button>
                            <button
                                type="button"
                                class="btn-primary"
                                :disabled="!basicDirty || saving"
                                @click="saveBasic"
                            >
                                <i v-if="saving" class="bi bi-arrow-repeat spin"></i>
                                <i v-else class="bi bi-check2"></i>
                                {{ saving ? $t('spheres.edit.basic.saving') : $t('spheres.edit.basic.save') }}
                            </button>
                        </div>
                    </div>
                </section>

                <!-- Section 2: Widget catalog -->
                <section class="section-card">
                    <div class="section-header">
                        <div class="section-label">
                            <i class="bi bi-grid-3x3-gap"></i>
                            {{ $t('spheres.edit.widgetsCatalog.sectionLabel') }}
                            <span class="section-meta">{{ $t('spheres.edit.widgetsCatalog.sectionMeta') }}</span>
                        </div>
                        <button type="button" class="btn-add-inline" @click="pickerOpen = true">
                            <i class="bi bi-plus-lg"></i>
                            {{ $t('spheres.edit.widgetsCatalog.addButton') }}
                        </button>
                    </div>

                    <div class="section-body">
                        <div v-if="!sphere.widgets.length" class="section-empty">
                            <i class="bi bi-grid-3x3-gap"></i>
                            <p>{{ $t('spheres.edit.widgetsCatalog.empty') }}</p>
                        </div>

                        <div v-else class="widget-chip-grid">
                            <div
                                v-for="w in sphere.widgets"
                                :key="w.sphere_widget_id"
                                class="widget-chip"
                            >
                                <img
                                    v-if="w.picture"
                                    :src="displayPicture(w.picture)"
                                    class="widget-chip-pic"
                                    :alt="w.name"
                                    @error="onPicError"
                                />
                                <span class="widget-chip-name">{{ w.name }}</span>
                                <span class="widget-chip-type">{{ w.widget_type }}</span>
                                <button
                                    type="button"
                                    class="widget-chip-remove"
                                    @click="removeWidget(w)"
                                    :title="$t('spheres.edit.widgetsCatalog.removeTitle', { name: w.name })"
                                >
                                    <i class="bi bi-x-lg"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Section 3: Step overrides -->
                <section class="section-card">
                    <div class="section-header">
                        <div class="section-label">
                            <i class="bi bi-magic"></i>
                            {{ $t('spheres.edit.stepOverrides.sectionLabel') }}
                            <span class="section-meta">{{ $t('spheres.edit.stepOverrides.sectionMeta') }}</span>
                        </div>
                        <div v-if="overridesDirty" class="section-dirty">
                            <i class="bi bi-circle-fill"></i>
                            {{ $t('spheres.edit.basic.unsavedChanges') }}
                        </div>
                    </div>

                    <div class="section-body">
                        <div
                            v-for="step in stepDefs"
                            :key="step.key"
                            class="step-override"
                        >
                            <button
                                type="button"
                                class="step-override-summary"
                                @click="toggleStep(step.key)"
                            >
                                <i
                                    class="bi"
                                    :class="openSteps[step.key] ? 'bi-chevron-down' : 'bi-chevron-right'"
                                ></i>
                                <span class="step-override-title">{{ step.title }}</span>
                                <span v-if="stepHasOverride(step.key)" class="step-override-badge">{{ $t('spheres.edit.stepOverrides.configuredBadge') }}</span>
                            </button>

                            <div v-if="openSteps[step.key]" class="step-override-body">
                                <div class="field">
                                    <label class="field-label">
                                        <i class="bi bi-image"></i>
                                        {{ $t('spheres.edit.stepOverrides.image') }}
                                    </label>
                                    <div v-if="getStepImagePreview(step.key)" class="step-image-preview">
                                        <img :src="getStepImagePreview(step.key)" alt="" />
                                        <button
                                            type="button"
                                            class="step-image-remove"
                                            @click="clearStepImage(step.key)"
                                            :disabled="stepUploading[step.key]"
                                            :title="$t('spheres.edit.stepOverrides.removeImageTitle')"
                                        >
                                            <i class="bi bi-trash"></i>
                                        </button>
                                    </div>
                                    <label
                                        v-else
                                        class="step-image-drop"
                                        :class="{ 'step-image-drop-busy': stepUploading[step.key] }"
                                    >
                                        <input
                                            type="file"
                                            accept="image/svg+xml,image/png,image/jpeg"
                                            :disabled="stepUploading[step.key]"
                                            @change="onStepImageChange(step.key, $event)"
                                        />
                                        <div class="step-image-drop-icon">
                                            <i v-if="stepUploading[step.key]" class="bi bi-arrow-repeat spin"></i>
                                            <i v-else class="bi bi-cloud-arrow-up"></i>
                                        </div>
                                        <div class="step-image-drop-title">
                                            {{ stepUploading[step.key] ? $t('spheres.edit.stepOverrides.uploading') : $t('spheres.edit.stepOverrides.uploadPrompt') }}
                                        </div>
                                        <div class="step-image-drop-meta">{{ $t('spheres.edit.stepOverrides.uploadMeta') }}</div>
                                    </label>
                                </div>

                                <div class="field">
                                    <label class="field-label">
                                        <i class="bi bi-type-h2"></i>
                                        {{ $t('spheres.edit.stepOverrides.heading') }}
                                    </label>
                                    <input
                                        type="text"
                                        class="field-input"
                                        v-model="overrides[step.key].heading"
                                        :placeholder="step.placeholderHeading"
                                        maxlength="200"
                                        @blur="saveOverrides"
                                    />
                                </div>

                                <div class="field">
                                    <label class="field-label">
                                        <i class="bi bi-card-text"></i>
                                        {{ $t('spheres.edit.stepOverrides.hint') }}
                                    </label>
                                    <textarea
                                        class="field-input"
                                        v-model="overrides[step.key].hint"
                                        :placeholder="step.placeholderHint"
                                        rows="2"
                                        maxlength="500"
                                        @blur="saveOverrides"
                                    ></textarea>
                                    <p v-if="step.key === 'objects'" class="field-hint">
                                        <i class="bi bi-info-circle"></i>
                                        {{ $t('spheres.edit.stepOverrides.objectsHint.usagePrefix') }}
                                        <code>{singular}</code> {{ $t('spheres.edit.stepOverrides.objectsHint.and') }} <code>{plural}</code>,
                                        {{ $t('spheres.edit.stepOverrides.objectsHint.orExample') }} <code>*{{ form.object_label_singular || $t('spheres.edit.stepOverrides.objectsHint.defaultSingular') }}*</code>
                                        {{ $t('spheres.edit.stepOverrides.objectsHint.and') }} <code>*{{ form.object_label_plural || $t('spheres.edit.stepOverrides.objectsHint.defaultPlural') }}*</code>.
                                    </p>
                                </div>

                                <!-- Objects-step-only: lets the admin tailor
                                     the "Название SuperQR" input placeholder
                                     per sphere (e.g. "Например: Кафе на Достык"
                                     vs "Например: QR в номерах" for hotels). -->
                                <div v-if="step.key === 'objects'" class="field">
                                    <label class="field-label">
                                        <i class="bi bi-input-cursor-text"></i>
                                        {{ $t('spheres.edit.stepOverrides.namePlaceholderLabel') }}
                                    </label>
                                    <input
                                        type="text"
                                        class="field-input"
                                        v-model="overrides[step.key].name_placeholder"
                                        :placeholder="$t('spheres.edit.stepOverrides.namePlaceholderExample')"
                                        maxlength="100"
                                        @blur="saveOverrides"
                                    />
                                    <p class="field-hint">
                                        <i class="bi bi-info-circle"></i>
                                        {{ $t('spheres.edit.stepOverrides.namePlaceholderHint') }}
                                    </p>
                                </div>

                                <div class="step-override-actions">
                                    <button
                                        type="button"
                                        class="btn-secondary btn-sm"
                                        :disabled="!stepHasOverride(step.key)"
                                        @click="resetStep(step.key)"
                                    >
                                        {{ $t('spheres.edit.stepOverrides.reset') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Section 4: Preferences -->
                <section class="section-card">
                    <div class="section-header">
                        <div class="section-label">
                            <i class="bi bi-list-ul"></i>
                            {{ $t('spheres.edit.preferences.sectionLabel') }}
                            <span class="section-meta">{{ $t('spheres.edit.preferences.sectionMeta') }}</span>
                        </div>
                        <button
                            v-if="!addingPref"
                            type="button"
                            class="btn-add-inline"
                            @click="addingPref = true"
                        >
                            <i class="bi bi-plus-lg"></i>
                            {{ $t('spheres.edit.preferences.addButton') }}
                        </button>
                    </div>

                    <div class="section-body">
                        <div v-if="addingPref" class="add-pref-form">
                            <input
                                type="text"
                                class="field-input"
                                v-model="newPrefLabel"
                                :placeholder="$t('spheres.edit.preferences.namePlaceholder')"
                                maxlength="255"
                                @keyup.enter="addPref"
                                autofocus
                            />
                            <button
                                type="button"
                                class="btn-primary btn-sm"
                                :disabled="!newPrefLabel.trim()"
                                @click="addPref"
                            >
                                {{ $t('spheres.edit.preferences.add') }}
                            </button>
                            <button
                                type="button"
                                class="btn-secondary btn-sm"
                                @click="addingPref = false; newPrefLabel = ''"
                            >
                                {{ $t('spheres.edit.preferences.cancel') }}
                            </button>
                        </div>

                        <div v-if="!sphere.preferences.length && !addingPref" class="section-empty">
                            <i class="bi bi-list-ul"></i>
                            <p>{{ $t('spheres.edit.preferences.empty') }}</p>
                        </div>

                        <PreferenceCard
                            v-for="p in sphere.preferences"
                            :key="p.id"
                            :preference="p"
                            :sphere-widgets="sphere.widgets"
                            :sphere-id="sphere.id"
                            @changed="reload(true)"
                        />
                    </div>
                </section>
            </div>
        </div>

        <WidgetPickerModal
            v-if="sphere"
            v-model="pickerOpen"
            :sphere-widgets="sphere.widgets"
            @add="addWidgets"
        />
    </div>
</template>

<script>
import IconPicker from './IconPicker.vue'
import PreferenceCard from './PreferenceCard.vue'
import WidgetPickerModal from './WidgetPickerModal.vue'

export default {
    name: 'EditSphere',
    components: { IconPicker, PreferenceCard, WidgetPickerModal },
    data: () => ({
        sphere: null,
        loading: true,
        saving: false,
        form: {
            name: '',
            slug: '',
            description: '',
            object_label_singular: '',
            object_label_plural: '',
            position: 0,
            enabled: true
        },
        // iconModel holds the picker's current state. iconDirty is set true the
        // moment the picker emits a change (we can't compare icon_url directly
        // because the API returns a presigned URL, not the raw S3 key).
        iconModel: { icon_slug: null, icon_url: null, preview_url: null },
        iconDirty: false,
        addingPref: false,
        newPrefLabel: '',
        pickerOpen: false,
        // Per-step customization for constructor wizard steps 2-4.
        // Each entry holds the editable text plus the storage key (image_url) and
        // a transient preview URL (preview_url) — same split as IconPicker so we
        // never re-PATCH presigned URLs back to the server.
        overrides: {
            preferences: { image_key: null, preview_url: null, heading: '', hint: '', name_placeholder: '' },
            objects:     { image_key: null, preview_url: null, heading: '', hint: '', name_placeholder: '' },
            widgets:     { image_key: null, preview_url: null, heading: '', hint: '', name_placeholder: '' }
        },
        openSteps: { preferences: false, objects: false, widgets: false },
        stepUploading: { preferences: false, objects: false, widgets: false },
        // Tracks whether the user touched any override field since last save.
        // We can't diff overrides vs sphere directly because backend hands back
        // presigned URLs, not raw S3 keys.
        overridesDirty: false
    }),
    watch: {
        iconModel: {
            handler(next, prev) {
                // Skip the initial assignment in copyToForm (prev will be the same ref/values)
                if (!this.sphere) return
                if (!prev) return
                if (next.icon_slug === prev.icon_slug && next.icon_url === prev.icon_url) return
                this.iconDirty = true
            },
            deep: true
        },
        overrides: {
            handler() {
                // Any nested change (text input, image swap, …) marks dirty.
                // _suspendOverridesWatch is set during copyToForm() so the
                // post-reload reassignment doesn't immediately re-flag dirty.
                if (!this.sphere) return
                if (this._suspendOverridesWatch) return
                this.overridesDirty = true
            },
            deep: true
        }
    },
    computed: {
        basicDirty() {
            if (!this.sphere) return false
            for (const k of Object.keys(this.form)) {
                if (this.form[k] !== this.sphere[k] && !(this.form[k] === '' && this.sphere[k] == null)) {
                    return true
                }
            }
            return this.iconDirty
        },
        headerIconSrc() {
            if (this.sphere.icon_url) return this.sphere.icon_url
            if (this.sphere.icon_slug) return this.iconSrc(this.sphere.icon_slug)
            return null
        },
        totalPreferences() {
            if (!this.sphere || !this.sphere.preferences) return 0
            let n = this.sphere.preferences.length
            for (const p of this.sphere.preferences) {
                n += (p.children ? p.children.length : 0)
            }
            return n
        },
        // Wizard step definitions for the "step overrides" section. Text is
        // sourced via $t() (not data()) so it reacts to a language switch —
        // data() would only evaluate the Russian literals once, at component
        // creation.
        stepDefs() {
            return [
                {
                    key: 'preferences',
                    title: this.$t('spheres.edit.stepDefs.preferences.title'),
                    placeholderHeading: this.$t('spheres.edit.stepDefs.preferences.placeholderHeading'),
                    placeholderHint: this.$t('spheres.edit.stepDefs.preferences.placeholderHint')
                },
                {
                    key: 'objects',
                    title: this.$t('spheres.edit.stepDefs.objects.title'),
                    placeholderHeading: this.$t('spheres.edit.stepDefs.objects.placeholderHeading'),
                    placeholderHint: ''
                },
                {
                    key: 'widgets',
                    title: this.$t('spheres.edit.stepDefs.widgets.title'),
                    placeholderHeading: this.$t('spheres.edit.stepDefs.widgets.placeholderHeading'),
                    placeholderHint: this.$t('spheres.edit.stepDefs.widgets.placeholderHint')
                }
            ]
        }
    },
    async mounted() {
        this.$emit('setPageTitle', this.$t('spheres.edit.pageTitle'))
        await this.reload()
    },
    methods: {
        iconSrc(slug) {
            return '/icons/spheres/' + slug + '.svg'
        },
        // silent=true skips the `loading` toggle so the page doesn't unmount
        // its scroll context — used by `@changed` handlers after small in-place
        // edits. The initial mount call leaves silent=false to show the
        // loading spinner the first time.
        async reload(silent = false) {
            if (!silent) this.loading = true
            const r = await this.$api.getSphere(this.$route.params.id)
            if (!silent) this.loading = false
            if (r.result === 'SUCCESS') {
                this.sphere = r.data
                this.copyToForm(r.data)
            } else {
                this.sphere = null
                this.$notify({ text: r.msg, type: 'error' })
            }
        },
        copyToForm(sphere) {
            this.form = {
                name: sphere.name || '',
                slug: sphere.slug || '',
                description: sphere.description || '',
                object_label_singular: sphere.object_label_singular || '',
                object_label_plural: sphere.object_label_plural || '',
                position: sphere.position || 0,
                enabled: !!sphere.enabled
            }
            this.iconModel = {
                icon_slug: sphere.icon_slug || null,
                // For an existing sphere with a custom upload, sphere.icon_url is a
                // presigned URL — we use it as the picker's preview but leave
                // iconModel.icon_url null so we don't accidentally re-PATCH it.
                icon_url: null,
                preview_url: sphere.icon_url || null
            }
            this.iconDirty = false

            // Hydrate per-step overrides. Backend gives us null when nothing
            // is set, otherwise { image_url: <presigned>, image_key: <raw S3
            // key>, heading, hint }. We keep both: image_key is what we PATCH
            // back; preview_url is what we render in the admin. The deep
            // watcher would normally flip overridesDirty when we reassign
            // here — suspend it for one tick so a fresh load isn't dirty.
            this._suspendOverridesWatch = true
            const blank = { image_key: null, preview_url: null, heading: '', hint: '', name_placeholder: '' }
            const so = sphere.step_overrides || {}
            for (const key of ['preferences', 'objects', 'widgets']) {
                const step = so[key]
                if (step) {
                    this.overrides[key] = {
                        image_key: step.image_key || null,
                        preview_url: step.image_url || null,
                        heading: step.heading || '',
                        hint: step.hint || '',
                        name_placeholder: step.name_placeholder || ''
                    }
                } else {
                    this.overrides[key] = { ...blank }
                }
            }
            this.overridesDirty = false
            this.$nextTick(() => { this._suspendOverridesWatch = false })
        },
        resetBasic() {
            this.copyToForm(this.sphere)
        },
        async saveBasic() {
            this.saving = true
            const patch = {}
            for (const k of Object.keys(this.form)) {
                const orig = this.sphere[k]
                const cur = this.form[k]
                if (cur !== orig && !(cur === '' && orig == null)) {
                    patch[k] = cur
                }
            }
            if (this.iconDirty) {
                patch.icon_slug = this.iconModel.icon_slug
                patch.icon_url = this.iconModel.icon_url
            }
            const r = await this.$api.patchSphere(this.sphere.id, patch)
            this.saving = false
            if (r.result === 'SUCCESS') {
                this.$notify({ text: this.$t('spheres.edit.notifications.saved'), type: 'success' })
                await this.reload(true)
            } else {
                this.$notify({ text: r.msg, type: 'error' })
            }
        },
        async addPref() {
            const label = this.newPrefLabel.trim()
            if (!label) return
            const r = await this.$api.addPreference(this.sphere.id, {
                label,
                position: this.sphere.preferences.length
            })
            if (r.result === 'SUCCESS') {
                this.newPrefLabel = ''
                this.addingPref = false
                await this.reload(true)
            } else {
                this.$notify({ text: r.msg, type: 'error' })
            }
        },
        // WidgetPickerModal emits an array of { widget_type, catalog_widget_id, title }
        // (one entry per selected catalog_widget instance). Each becomes a
        // separate sphere_widgets row so the sphere can carry e.g. Discord +
        // MAX + Viber as three distinct link instances.
        // Rewrite cabinet-relative widget pictures (../images/widget/x.svg)
        // to absolute cabinet URLs for the admin preview. Same helper as
        // components/catalog/Catalog.vue.
        displayPicture(p) {
            if (!p) return ''
            if (/^https?:\/\//.test(p)) return p
            if (p.startsWith('data:')) return p
            const cleaned = p.replace(/^(\.\.\/)+/, '/')
            const path = cleaned.startsWith('/') ? cleaned : '/' + cleaned
            return 'https://cabinet.superqr.com' + path
        },
        onPicError(e) { e.target.style.opacity = 0.25 },

        async addWidgets(instances) {
            for (const inst of instances) {
                const r = await this.$api.addSphereWidget(this.sphere.id, inst.widget_type, inst.catalog_widget_id)
                if (r.result !== 'SUCCESS') {
                    this.$notify({ text: r.msg, type: 'error' })
                }
            }
            await this.reload(true)
        },
        async removeWidget(widget) {
            const r = await this.$api.removeSphereWidget(this.sphere.id, widget.widget_type, widget.catalog_widget_id)
            if (r.result === 'SUCCESS') {
                await this.reload(true)
            } else {
                this.$notify({ text: r.msg, type: 'error' })
            }
        },
        toggleStep(key) {
            this.openSteps[key] = !this.openSteps[key]
        },
        getStepImagePreview(key) {
            const o = this.overrides[key]
            return o.preview_url || null
        },
        stepHasOverride(key) {
            const o = this.overrides[key]
            return !!(o.preview_url
                || (o.heading && o.heading.trim())
                || (o.hint && o.hint.trim())
                || (o.name_placeholder && o.name_placeholder.trim()))
        },
        clearStepImage(key) {
            this.overrides[key].image_key = null
            this.overrides[key].preview_url = null
            this.overridesDirty = true
            this.saveOverrides()
        },
        resetStep(key) {
            this.overrides[key] = { image_key: null, preview_url: null, heading: '', hint: '', name_placeholder: '' }
            this.overridesDirty = true
            this.saveOverrides()
        },
        async onStepImageChange(key, e) {
            const file = e.target.files && e.target.files[0]
            if (!file) return
            if (file.size > 2 * 1024 * 1024) {
                this.$notify({ text: this.$t('spheres.edit.notifications.fileTooLarge'), type: 'error' })
                e.target.value = ''
                return
            }
            this.stepUploading[key] = true
            try {
                const fd = new FormData()
                fd.append('icon', file)
                const url = process.env.VUE_APP_API_URL + '/spheres/upload-icon?prefix=sphere-step-images'
                const res = await fetch(url, {
                    method: 'POST',
                    credentials: 'include',
                    body: fd
                })
                const json = await res.json()
                if (json.result === 'SUCCESS') {
                    this.overrides[key].image_key = json.data.icon_url
                    this.overrides[key].preview_url = json.data.preview_url || null
                    this.overridesDirty = true
                    this.$notify({ text: this.$t('spheres.edit.notifications.imageUploaded'), type: 'success' })
                    await this.saveOverrides()
                } else {
                    this.$notify({ text: json.msg || this.$t('spheres.edit.notifications.uploadFailed'), type: 'error' })
                }
            } catch (err) {
                this.$notify({ text: err.message || this.$t('spheres.edit.notifications.uploadError'), type: 'error' })
            } finally {
                this.stepUploading[key] = false
                e.target.value = ''
            }
        },
        // PATCH the sphere with the current step_overrides snapshot. Empty
        // fields collapse to null on the server; a step with all three null
        // is stored as null. We send `image_key` (the raw S3 key) so the
        // server stores the storage key — never the presigned preview URL.
        async saveOverrides() {
            if (!this.overridesDirty) return
            const overrides = {}
            for (const key of ['preferences', 'objects', 'widgets']) {
                const o = this.overrides[key]
                const heading = (o.heading || '').trim() || null
                const hint = (o.hint || '').trim() || null
                const namePlaceholder = (o.name_placeholder || '').trim() || null
                const imageKey = o.image_key || null
                if (imageKey === null && heading === null && hint === null && namePlaceholder === null) {
                    overrides[key] = null
                } else {
                    overrides[key] = { image_key: imageKey, heading, hint, name_placeholder: namePlaceholder }
                }
            }
            this.overridesDirty = false
            const r = await this.$api.patchSphere(this.sphere.id, { step_overrides: overrides })
            if (r.result === 'SUCCESS') {
                await this.reload(true)
            } else {
                this.overridesDirty = true
                this.$notify({ text: r.msg, type: 'error' })
            }
        }
    }
}
</script>

<style scoped>
.page-wrap {
    max-width: 960px;
    margin: 0 auto;
    padding: 24px 0;
}

.back-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #64748b;
    font-size: 13px;
    font-weight: 500;
    text-decoration: none;
    margin-bottom: 16px;
    transition: color 0.15s ease;
}
.back-link:hover {
    color: #7c3aed;
}
.back-link i {
    font-size: 14px;
}

/* States */
.state-loading {
    text-align: center;
    padding: 64px 20px;
    color: #64748b;
}
.state-loading .spinner-border {
    color: #7c3aed;
    width: 2rem;
    height: 2rem;
}
.state-loading p {
    margin: 16px 0 0;
    font-size: 14px;
}

.state-warning {
    background: #fef3c7;
    border: 1px solid #fde68a;
    color: #92400e;
    border-radius: 12px;
    padding: 14px 16px;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
}

/* Page header */
.page-header {
    display: flex;
    align-items: center;
    gap: 16px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    padding: 18px 22px;
    margin-bottom: 18px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.page-header-icon {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background: #faf7ff;
    border: 1px solid #ede9fe;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}
.page-header-icon img {
    width: 36px;
    height: 36px;
    object-fit: contain;
}
.page-header-icon i {
    font-size: 22px;
    color: #c4b5fd;
}
.page-header-main {
    flex: 1;
    min-width: 0;
}
.page-header-titleline {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 6px;
    flex-wrap: wrap;
}
.page-header-title {
    font-size: 20px;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
}
.page-header-slug {
    font-size: 11px;
    font-family: ui-monospace, monospace;
    color: #94a3b8;
    background: #f1f5f9;
    padding: 2px 8px;
    border-radius: 4px;
}
.page-header-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 999px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
}
.status-pill-on {
    background: #dcfce7;
    color: #15803d;
}
.status-pill-on .status-dot {
    background: #22c55e;
}
.status-pill-off {
    background: #f1f5f9;
    color: #64748b;
}
.status-pill-off .status-dot {
    background: #94a3b8;
}
.meta-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 500;
    color: #475569;
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    padding: 3px 9px;
    border-radius: 6px;
}
.meta-item i {
    font-size: 11px;
    color: #7c3aed;
}

/* Section card */
.section-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    margin-bottom: 18px;
    overflow: hidden;
}
.section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 22px;
    border-bottom: 1px solid #f1f5f9;
    flex-wrap: wrap;
}
.section-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.section-label i {
    font-size: 14px;
    color: #7c3aed;
}
.section-meta {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 400;
    color: #94a3b8;
    margin-left: 4px;
}
.section-dirty {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #b45309;
    background: #fef3c7;
    padding: 3px 10px;
    border-radius: 999px;
}
.section-dirty i {
    font-size: 6px;
}
.section-body {
    padding: 22px;
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.section-empty {
    text-align: center;
    padding: 32px 16px;
    background: #fafbfc;
    border: 1px dashed #e5e7eb;
    border-radius: 12px;
    color: #94a3b8;
}
.section-empty i {
    font-size: 28px;
    margin-bottom: 8px;
    color: #c4b5fd;
    display: inline-block;
}
.section-empty p {
    margin: 0;
    font-size: 13px;
}

/* Fields */
.field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.field-hint {
    margin: 4px 0 0;
    font-size: 12px;
    line-height: 1.5;
    color: #64748b;
    display: flex;
    gap: 6px;
    align-items: flex-start;
}
.field-hint i {
    color: #7c3aed;
    margin-top: 2px;
    flex-shrink: 0;
}
.field-hint code {
    background: #f1f5f9;
    color: #6d28d9;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 11px;
    font-family: ui-monospace, monospace;
}
.field-row {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
}
.field-row > .field {
    flex: 1;
    min-width: 200px;
}
.field-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin: 0;
}
.field-label i {
    font-size: 14px;
    color: #7c3aed;
}
.field-input {
    width: 100%;
    padding: 9px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    font-size: 14px;
    color: #1e293b;
    background: #fff;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    font-family: inherit;
}
.field-input::placeholder {
    color: #94a3b8;
}
.field-input:hover {
    border-color: #cbd5e1;
}
.field-input:focus {
    outline: none;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.15);
}
.field-input-mono {
    font-family: ui-monospace, SFMono-Regular, monospace;
    font-size: 13px;
}
textarea.field-input {
    resize: vertical;
    min-height: 60px;
}

/* Toggle switch */
.field-toggle .toggle {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    user-select: none;
    padding: 6px 0;
}
.toggle input[type="checkbox"] {
    position: absolute;
    opacity: 0;
    pointer-events: none;
}
.toggle-slider {
    width: 40px;
    height: 22px;
    background: #cbd5e1;
    border-radius: 999px;
    position: relative;
    transition: background 0.15s ease;
    flex-shrink: 0;
}
.toggle-slider::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    background: #fff;
    border-radius: 50%;
    transition: transform 0.15s ease;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.2);
}
.toggle input:checked + .toggle-slider {
    background: #7c3aed;
}
.toggle input:checked + .toggle-slider::after {
    transform: translateX(18px);
}
.toggle-label {
    font-size: 14px;
    color: #1e293b;
    font-weight: 500;
}

/* Section actions */
.section-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 4px;
}

/* Buttons */
.btn-primary,
.btn-secondary {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 9px 18px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    border: 1px solid transparent;
    background: transparent;
}
.btn-sm {
    padding: 7px 14px;
    font-size: 13px;
}
.btn-primary {
    background: #7c3aed;
    border-color: #7c3aed;
    color: #fff;
    box-shadow: 0 1px 2px rgba(124, 58, 237, 0.2);
}
.btn-primary:hover:not(:disabled) {
    background: #6d28d9;
    border-color: #6d28d9;
    box-shadow: 0 4px 12px rgba(124, 58, 237, 0.25);
}
.btn-primary:disabled {
    background: #c4b5fd;
    border-color: #c4b5fd;
    cursor: not-allowed;
    box-shadow: none;
}
.btn-secondary {
    background: #fff;
    border-color: #e5e7eb;
    color: #475569;
}
.btn-secondary:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #1e293b;
}
.btn-secondary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-add-inline {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: transparent;
    border: 1.5px dashed #cbd5e1;
    color: #64748b;
    padding: 7px 14px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
}
.btn-add-inline:hover {
    border-color: #7c3aed;
    color: #7c3aed;
    background: #faf7ff;
}
.btn-add-inline i {
    font-size: 13px;
}

/* Widget chips */
.widget-chip-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.widget-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 4px 6px 4px 4px;
    background: #fff;
    border: 1.5px solid #e5e7eb;
    border-radius: 999px;
    transition: all 0.12s ease;
}
.widget-chip:hover {
    border-color: #c4b5fd;
    background: #faf7ff;
}
.widget-chip-pic {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    object-fit: cover;
    background: #f1f5f9;
    flex-shrink: 0;
}
.widget-chip-name {
    font-size: 13px;
    font-weight: 500;
    color: #1e293b;
}
.widget-chip-type {
    font-size: 10px;
    font-family: ui-monospace, monospace;
    color: #94a3b8;
    background: #f1f5f9;
    padding: 2px 6px;
    border-radius: 4px;
}
.widget-chip-remove {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: #94a3b8;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.12s ease;
    font-size: 10px;
}
.widget-chip-remove:hover {
    background: #fef2f2;
    color: #dc2626;
}

/* Add pref form */
.add-pref-form {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
    background: #faf7ff;
    border: 1px solid #ede9fe;
    border-radius: 10px;
    padding: 10px;
}
.add-pref-form .field-input {
    flex: 1;
    min-width: 200px;
}

.spin {
    animation: es-spin 1s linear infinite;
}
@keyframes es-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

/* Step override expanders */
.step-override {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    overflow: hidden;
    transition: border-color 0.15s ease;
}
.step-override + .step-override {
    margin-top: 0;
}
.step-override:hover {
    border-color: #ddd6fe;
}
.step-override-summary {
    width: 100%;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
    transition: background 0.12s ease;
}
.step-override-summary:hover {
    background: #faf7ff;
}
.step-override-summary i {
    font-size: 14px;
    color: #7c3aed;
    transition: transform 0.15s ease;
    flex-shrink: 0;
}
.step-override-title {
    flex: 1;
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
}
.step-override-badge {
    font-size: 11px;
    font-weight: 600;
    color: #6d28d9;
    background: #f5f0ff;
    border: 1px solid #ede9fe;
    padding: 2px 9px;
    border-radius: 999px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.step-override-body {
    padding: 14px 16px 16px;
    border-top: 1px solid #f1f5f9;
    display: flex;
    flex-direction: column;
    gap: 14px;
    background: #fafbfc;
}
.step-override-actions {
    display: flex;
    justify-content: flex-end;
}

/* Step image upload (inline, no preset tab) */
.step-image-preview {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #fff;
    border: 1px solid #ede9fe;
    border-radius: 12px;
    padding: 10px;
}
.step-image-preview img {
    max-width: 240px;
    max-height: 120px;
    border-radius: 8px;
    object-fit: cover;
    flex-shrink: 0;
    background: #f1f5f9;
}
.step-image-remove {
    margin-left: auto;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid #fecaca;
    background: #fff;
    color: #dc2626;
    cursor: pointer;
    transition: all 0.15s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}
.step-image-remove:hover:not(:disabled) {
    background: #fef2f2;
    border-color: #dc2626;
}
.step-image-remove:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
.step-image-drop {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
    background: #fff;
    border: 2px dashed #cbd5e1;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.15s ease;
    text-align: center;
    margin: 0;
}
.step-image-drop:hover {
    border-color: #7c3aed;
    background: #faf7ff;
}
.step-image-drop-busy {
    cursor: wait;
    border-color: #7c3aed;
    background: #faf7ff;
}
.step-image-drop input[type="file"] {
    display: none;
}
.step-image-drop-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #f5f0ff;
    color: #7c3aed;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    margin-bottom: 8px;
}
.step-image-drop-title {
    font-size: 13px;
    font-weight: 500;
    color: #1e293b;
    margin-bottom: 2px;
}
.step-image-drop-meta {
    font-size: 12px;
    color: #94a3b8;
}
</style>
